// RND-7: entity rendering. Context is expected in WORLD transform (setTransform(dpr·scale,...,tx,ty))
// so all drawing is in mm; screen-constant values divide by scale.
import type { Camera } from "./camera";
import type { Doc, Entity, Layer, MarkerEntity, Style } from "../doc/entities";
import { cloudScallops } from "../core/marker";
import { renderDimension, renderGrid, renderLabel, renderText } from "./renderAnnotation";

export interface RenderView {
  ctx: CanvasRenderingContext2D;
  cam: Camera;
  cssW: number;
  cssH: number;
  dpr: number;
  /**
   * Export only: world mm per "dimension mm". Set so dimensions are as big next to the drawing
   * as they are on the screen; unset = constant pixels at this view's zoom.
   */
  dimPm?: number;
}

/** Set the world transform on a 2D context (RND-3). */
export function applyWorldTransform(v: RenderView): void {
  const k = v.dpr * v.cam.scale;
  v.ctx.setTransform(k, 0, 0, k, v.dpr * (v.cssW / 2 - v.cam.cx * v.cam.scale), v.dpr * (v.cssH / 2 - v.cam.cy * v.cam.scale));
}

/** Effective world stroke width incl. hairline minimum (RND-7). */
export function strokeWidthWorld(style: Style, cam: Camera, dpr: number): number {
  const w = style.widthMode === "world" ? style.strokeWidth : style.strokeWidth / cam.scale;
  return Math.max(w, 1 / (dpr * cam.scale)); // ≥ 1 physical px
}

/** DM-3 dash patterns in world units; EC-RND-6: solid below 0.5 px dash size. */
export function dashPattern(style: Style, w: number, cam: Camera): number[] {
  if (style.dash === "solid") return [];
  if (w * cam.scale < 0.5) return [];
  switch (style.dash) {
    case "dashed":
      return [4 * w, 2 * w];
    case "dotted":
      return [0.5 * w, 2 * w];
    case "dashdot":
      return [6 * w, 2 * w, 0.5 * w, 2 * w];
  }
  return [];
}

export function tracePath(ctx: CanvasRenderingContext2D, entity: Entity): boolean {
  switch (entity.type) {
    case "polyline":
    case "marker": {
      const pts = entity.points;
      if (pts.length < 2) return false;
      ctx.beginPath();
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
      if (entity.type === "polyline" && entity.closed) ctx.closePath();
      return true;
    }
    case "circle":
      ctx.beginPath();
      ctx.arc(entity.center.x, entity.center.y, entity.r, 0, Math.PI * 2);
      return true;
    case "arc": {
      // ARC-4 angles are CW-positive under Y-down, which matches canvas arc() angles exactly.
      const a0 = (entity.startDeg * Math.PI) / 180;
      const a1 = ((entity.startDeg + entity.sweepDeg) * Math.PI) / 180;
      ctx.beginPath();
      ctx.arc(entity.center.x, entity.center.y, entity.r, a0, a1, entity.sweepDeg < 0);
      return true;
    }
    default:
      return false; // text/label/image/grid/dimension render in later milestones
  }
}

/** Draw one entity (styled). Images render on the bg layer (renderImages), never here. */
export function drawEntity(v: RenderView, entity: Entity, layer: Layer | undefined, doc: Doc): void {
  if (entity.type === "group") {
    for (const childId of entity.children) {
      const child = doc.entities[childId];
      if (child && !child.hidden) drawEntity(v, child, layer, doc);
    }
    return;
  }
  switch (entity.type) {
    case "text":
      renderText(v, entity, layer);
      return;
    case "label":
      renderLabel(v, entity, layer);
      return;
    case "dimension":
      renderDimension(v, entity, doc, layer);
      return;
    case "grid":
      renderGrid(v, entity, layer);
      return;
    case "marker":
      if (entity.kind === "cloud") {
        renderCloud(v, entity, layer);
        return;
      }
      break; // highlight → generic stroke path below (opacity carried in style, F2)
    default:
      break;
  }
  if (!("style" in entity)) return;
  const { ctx } = v;
  if (!tracePath(ctx, entity)) return;

  const style = entity.style;
  const layerAlpha = layer?.opacity ?? 1;
  const w = strokeWidthWorld(style, v.cam, v.dpr);

  const fillable =
    entity.type === "circle" || (entity.type === "polyline" && entity.closed);
  if (fillable && style.fill.mode !== "none" && style.fill.opacity > 0) {
    // EC-RND-8 covered by the opacity check above
    ctx.globalAlpha = style.fill.opacity * layerAlpha;
    const fill = style.fill.mode === "solid" ? style.fill.color : getFillPattern(ctx, style.fill.mode, style.fill.color);
    // 3.20.0: the tile is 4 mm, but never smaller than 10 screen pixels. A smaller tile is under a pixel at plan zoom:
    // the hatch then aliased into stray stripes near the origin and faded to nothing further away (a wall at x 20 000).
    if (typeof fill !== "string") fill.setTransform(patternMatrix(v.cam.scale));
    ctx.fillStyle = fill;
    ctx.fill();
  }

  ctx.globalAlpha = style.opacity * layerAlpha;
  ctx.strokeStyle = style.strokeColor;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.setLineDash(dashPattern(style, style.widthMode === "world" ? style.strokeWidth : w, v.cam));
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

const CLOUD_RADIUS_MM = 6; // TL-6 revision-cloud scallop radius

/** TL-6 revision cloud: scalloped rectangle between the 2 stored bbox corners. */
function renderCloud(v: RenderView, entity: MarkerEntity, layer: Layer | undefined): void {
  if (entity.points.length < 2) return;
  const pts = cloudScallops(entity.points[0]!, entity.points[1]!, CLOUD_RADIUS_MM);
  if (pts.length < 2) return;
  const { ctx } = v;
  ctx.globalAlpha = entity.style.opacity * (layer?.opacity ?? 1);
  ctx.strokeStyle = entity.style.strokeColor;
  ctx.lineWidth = strokeWidthWorld(entity.style, v.cam, v.dpr);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
  ctx.closePath();
  ctx.stroke();
  ctx.globalAlpha = 1;
}

// RND-7: hatch/crosshatch/dots as cached CanvasPatterns. Tile = 64 px drawn crisp, then
// scaled to a 4 mm world tile via pattern transform so fills zoom with geometry.
const patternCache = new Map<string, CanvasPattern>();

/** The smallest pattern tile on the screen, CSS px (3.20.0). */
export const MIN_PATTERN_TILE_PX = 10;

/** The world size of a pattern tile at `scale` px/mm: 4 mm, or more when 4 mm is under MIN_PATTERN_TILE_PX. */
export function patternTileMm(scale: number): number {
  return Math.max(4, MIN_PATTERN_TILE_PX / Math.max(scale, 1e-9));
}

function patternMatrix(scale: number): DOMMatrix {
  const k = patternTileMm(scale) / 64;
  return new DOMMatrix([k, 0, 0, k, 0, 0]);
}

function getFillPattern(
  target: CanvasRenderingContext2D,
  mode: "hatch" | "crosshatch" | "dots",
  color: string
): CanvasPattern | string {
  const key = `${mode}:${color}`;
  const cached = patternCache.get(key);
  if (cached) return cached;
  const tile = document.createElement("canvas");
  tile.width = tile.height = 64;
  const t = tile.getContext("2d");
  if (!t) return color;
  t.strokeStyle = color;
  t.fillStyle = color;
  t.lineWidth = 4;
  if (mode === "hatch" || mode === "crosshatch") {
    t.beginPath();
    for (const d of [-64, 0, 64]) {
      t.moveTo(d, 64);
      t.lineTo(d + 64, 0); // 45° per DM-3
    }
    t.stroke();
  }
  if (mode === "crosshatch") {
    t.beginPath();
    for (const d of [-64, 0, 64]) {
      t.moveTo(d, 0);
      t.lineTo(d + 64, 64);
    }
    t.stroke();
  }
  if (mode === "dots") {
    for (const [cx, cy] of [
      [16, 16],
      [48, 48]
    ]) {
      t.beginPath();
      t.arc(cx!, cy!, 6, 0, Math.PI * 2);
      t.fill();
    }
  }
  const pattern = target.createPattern(tile, "repeat");
  if (!pattern) return color;
  pattern.setTransform(new DOMMatrix([4 / 64, 0, 0, 4 / 64, 0, 0])); // 4 mm world tile
  patternCache.set(key, pattern);
  return pattern;
}

/** Render all visible entities in draw order (RND-6), skipping `skipIds` (active edit set). */
export function renderDocEntities(v: RenderView, doc: Doc, skipIds: ReadonlySet<string>): void {
  applyWorldTransform(v);
  const groupChildren = new Set<string>();
  for (const e of Object.values(doc.entities)) {
    if (e.type === "group") for (const c of e.children) groupChildren.add(c);
  }
  for (const layer of doc.layers) {
    if (!layer.visible) continue;
    for (const id of doc.zOrder[layer.id] ?? []) {
      if (skipIds.has(id) || groupChildren.has(id)) continue;
      const entity = doc.entities[id];
      if (!entity || entity.hidden) continue;
      drawEntity(v, entity, layer, doc);
    }
  }
}
